/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import CallingWidget from './CallingWidget';
import { useEffect } from 'react';
import './animation.css';
import SelectedItemsUI from './SelectedItemsUI';
import ResoAssistResolutionWidget from './ResoAssistResolutionWidget';
import { useRef } from 'react';
import { useCallback } from 'react';
import { MAX_SSE_RETRIES, RETRY_SSE_INTERVAL_MS } from '../constants';
import { useState } from 'react';

const ResoAssistScreen = ({
  coreEvaluate,
  resoAssistEvaluate,
  resoAssistDetails = {},
  setShowResolutionOptions,
  handleComplaintImgvisibilty,
  orderId,
  userId,
  cartId,
  complaintType,
}) => {
  const [details, setDetails] = useState(resoAssistDetails);
  const [isStateUpdating, setIsStateUpdating] = useState(false);

  const {
    item_thumbnails,
    reso_assist_summary,
    is_call_required: isCallRequired,
    sm_calling_details,
    dp_calling_details,
    reso_assist_resolution_details,
  } = details || {};

  const sseStartedRef = useRef(false);
  const eventSourceRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef(null);
  const [updatedRTCallStatus, setUpdatedRTCallStatus] = useState({
    text: '',
    text_color: '',
    callee: '',
  });

  const updateCallStatus = (callee, response) => {
    let callingResponse = {};
    if (callee === 'SM') {
      callingResponse.sm_response = response;
    } else if (callee === 'DP') {
      callingResponse.dp_response = response;
    }
    resoAssistEvaluate(callingResponse, false, true);
    setIsStateUpdating(true);
  };

  const editItemSelection = () => {
    setShowResolutionOptions();
    handleComplaintImgvisibilty();
  };

  const handleResoAssistProceed = () => {
    coreEvaluate(false, true);
  };

  const startSSE = React.useCallback(
    (callee) => {
      if (sseStartedRef.current || eventSourceRef.current) return;

      sseStartedRef.current = true;

      const sm_required = callee == 'SM' ? 'true' : 'false';
      const dp_required = callee == 'DP' ? 'true' : 'false';

      const callStatusUpdateEvent = new EventSource(
        `/api/crm_proxy/lifeline_apis/issues/v2/reso_assist/call_status_stream?order_id=${orderId}&sm_required=${sm_required}&dp_required=${dp_required}`,
      );

      callStatusUpdateEvent.onopen = () => {
        retryCountRef.current = 0;
      };

      callStatusUpdateEvent.onmessage = (event) => {
        if (!event.data) return;

        try {
          const parsedCallStatus = JSON.parse(event.data);
          const { text, text_color, callee } = parsedCallStatus;
          setUpdatedRTCallStatus({ text, text_color, callee });
        } catch (error) {
          console.error('Error parsing SSE data:', error);
        }
      };

      callStatusUpdateEvent.onerror = () => {
        callStatusUpdateEvent.close();
        eventSourceRef.current = null;
        sseStartedRef.current = false;

        if (retryCountRef.current < MAX_SSE_RETRIES) {
          retryCountRef.current += 1;
          retryTimeoutRef.current = setTimeout(() => {
            retryTimeoutRef.current = null;
            startSSE();
          }, RETRY_SSE_INTERVAL_MS);
        }
      };

      eventSourceRef.current = callStatusUpdateEvent;
    },
    [orderId, sm_calling_details, dp_calling_details],
  );

  const onCallInitiated = useCallback(
    (callee) => {
      if (isCallRequired) {
        startSSE(callee);
      }
    },
    [isCallRequired, startSSE],
  );

  const updatedCallStatus = useCallback(
    (callee) => {
      if (updatedRTCallStatus?.callee === callee) {
        return {
          call_status: {
            text: updatedRTCallStatus.text,
            text_color: updatedRTCallStatus.text_color,
          },
        };
      }
      return {};
    },
    [updatedRTCallStatus],
  );

  const callingDetails = useCallback(
    (callee) => {
      if (callee === 'SM') {
        return { ...(sm_calling_details || {}), ...updatedCallStatus(callee) };
      } else if (callee === 'DP') {
        return { ...(dp_calling_details || {}), ...updatedCallStatus(callee) };
      }
      return {};
    },
    [sm_calling_details, dp_calling_details, updatedCallStatus],
  );

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setDetails(resoAssistDetails);
    setIsStateUpdating(false);
  }, [resoAssistDetails]);

  return (
    <div className='tw-p-8 tw-flex tw-flex-col tw-overflow-auto'>
      {/* Selected Items Details and Reso summary */}
      <SelectedItemsUI
        item_thumbnails={item_thumbnails}
        reso_assist_summary={reso_assist_summary}
        editItemSelection={editItemSelection}
      />

      {isCallRequired &&
        ['SM', 'DP'].map((callee) => (
          <CallingWidget
            key={callee}
            orderId={orderId}
            userId={userId}
            cartId={cartId}
            callingTo={callee}
            updateCallStatus={updateCallStatus}
            onCallInitiated={onCallInitiated}
            {...callingDetails(callee)}
            isStateUpdating={isStateUpdating}
          />
        ))}

      <ResoAssistResolutionWidget
        orderId={orderId}
        userId={userId}
        cartId={cartId}
        complaintType={complaintType}
        resoAssistResolutionDetails={reso_assist_resolution_details}
        handleResoAssistProceed={handleResoAssistProceed}
        isStateUpdating={isStateUpdating}
        resoAssistDetails={resoAssistDetails}
      />
    </div>
  );
};

export default ResoAssistScreen;
